import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState, AppDispatch } from "../../../../../redux/store";
import { fetchCategories, upsertCategory } from "../../../../../redux/blogSlice";

const ECategory: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const categories = useSelector((state: RootState) => state.blog.categories);

  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [description, setDescription] = useState("");
  const [parent, setParent] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    dispatch(fetchCategories());
  }, [dispatch]);

  const generateSlug = (text: string) =>
    text
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, "")
      .replace(/\s+/g, "-");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    const finalSlug = slug.trim() !== "" ? slug : generateSlug(name);
    try {
      await dispatch(
        upsertCategory({
          title: name,
          slug: finalSlug,
          description,
          parent,
        })
      ).unwrap();
      setName("");
      setSlug("");
      setDescription("");
      setParent(null);
    } catch (err) {
      console.error("Failed to save category", err);
    }
    setLoading(false);
  };

  return (
    <div style={styles.container}>
      <h2 style={styles.heading}>Add Category</h2>
      <form onSubmit={handleSubmit} style={styles.form}>
        <label style={styles.label}>Name</label>
        <input
          style={styles.input}
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="The name is how it appears on your site."
        />

        <label style={styles.label}>Slug</label>
        <input
          style={styles.input}
          value={slug}
          onChange={(e) => setSlug(e.target.value)}
          placeholder="URL-friendly version (e.g. jazz)"
        />

        <label style={styles.label}>Parent Category</label>
        <select
          style={styles.select}
          value={parent || ""}
          onChange={(e) => setParent(e.target.value || null)}
        >
          <option value="">None</option>
          {categories.map((cat) => (
            <option key={cat.id} value={cat.slug}>
              {cat.title}
            </option>
          ))}
        </select>
        <p style={styles.note}>
          Categories, unlike tags, can have a hierarchy. You might have a Jazz category, and under that children
          like Bebop and Big Band.
        </p>

        <label style={styles.label}>Description</label>
        <textarea
          style={styles.textarea}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="This description is optional but may be shown by some themes."
        />

        <button style={styles.button} type="submit" disabled={loading}>
          {loading ? "Saving..." : "Add Category"}
        </button>
      </form>
    </div>
  );
};

export default ECategory;

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    padding: "2rem",
    background: "linear-gradient(160deg, #0a0014, #1e003a, #a78bfa, #111)",
    color: "#fff",
    minHeight: "100vh",
    fontFamily: "'Segoe UI', sans-serif",
  },
  heading: {
    fontSize: "24px",
    fontWeight: 700,
    marginBottom: "1.5rem",
    textAlign: "center",
  },
  form: {
    maxWidth: 500,
    margin: "0 auto",
    display: "flex",
    flexDirection: "column",
    gap: "1rem",
    backgroundColor: "rgba(255,255,255,0.05)",
    padding: "1.5rem",
    borderRadius: 12,
    boxShadow: "0 8px 30px rgba(168, 85, 247, 0.1)",
  },
  label: {
    fontWeight: "bold",
    fontSize: "14px",
  },
  input: {
    padding: "10px",
    borderRadius: 8,
    border: "1px solid #555",
    background: "#1e1e2f",
    color: "#fff",
  },
  select: {
    padding: "10px",
    borderRadius: 8,
    background: "#1e1e2f",
    color: "#fff",
    border: "1px solid #555",
  },
  textarea: {
    padding: "10px",
    minHeight: 80,
    borderRadius: 8,
    border: "1px solid #555",
    background: "#1e1e2f",
    color: "#fff",
  },
  note: {
    fontSize: "12px",
    color: "#bbb",
  },
  button: {
    background: "linear-gradient(to right, #a78bfa, #c084fc)",
    color: "#000",
    padding: "10px",
    border: "none",
    borderRadius: 8,
    fontWeight: "bold",
    cursor: "pointer",
    transition: "0.3s",
  },
};
